import { BadgePercent, CalendarClock, Gift, Users, Phone, MessageCircle, Sparkles } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { BRAND } from "@/data/site";

const TIERS = [
  { name: "Bronze", price: "₹12,000", months: "3 months", therapies: 7, highlight: false },
  { name: "Silver", price: "₹17,000", months: "6 months", therapies: 12, highlight: false },
  { name: "Gold", price: "₹29,000", months: "9 months", therapies: 22, highlight: true },
  { name: "Platinum", price: "₹35,000", months: "12 months", therapies: 32, highlight: false },
  { name: "Titanium", price: "₹50,000", months: "18 months", therapies: 60, highlight: false },
  { name: "Diamond", price: "₹1,00,000", months: "34 months", therapies: 135, highlight: false },
];

const purchaseLink = (t) =>
  `https://wa.me/919014729756?text=${encodeURIComponent(
    `Hi Ody Luxurious Spa, I would like to purchase the ${t.name} Membership — ${t.price} / ${t.months}, ${t.therapies} therapies. Please share the details.`
  )}`;

const PERKS = [
  { icon: CalendarClock, title: "Priority slots", text: "Members book first — evenings and weekends included." },
  { icon: BadgePercent, title: "Member rates", text: "Your therapies are pre-loaded, so every visit costs less per session." },
  { icon: Gift, title: "Gifting built in", text: "Gift a session from your plan to someone who needs it." },
  { icon: Users, title: "Couple privileges", text: "Use your therapies in our private couple suites too." },
];

const STEPS = [
  { title: "Pick your plan", text: "Choose a tier below and tap Purchase — your request opens in WhatsApp, ready to send." },
  { title: "We activate it", text: "Visit us once, complete activation at the spa, and your therapies are loaded to your name." },
  { title: "Book & unwind", text: "Reserve slots on call or WhatsApp anytime during your plan period. Simply arrive and relax." },
];

export default function Membership() {
  return (
    <>
      <Seo
        title="Premium Spa Memberships from ₹12,000 | Ody Luxurious Spa, Manikonda"
        description="Prepaid spa memberships at Ody Luxurious Spa, Manikonda — Bronze to Diamond plans with 7 to 135 therapies. Priority slots, member rates and couple privileges."
      />
      <PageHero
        eyebrow="ODY Membership"
        title="Stillness, on a schedule"
        subtitle="Prepaid therapy plans that make calm a habit — the longer your plan, the more every session saves you."
        crumbs={[{ label: "Membership" }]}
      />

      <section className="py-20 sm:py-28 bg-alabaster">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="text-center text-xs uppercase tracking-[0.35em] font-medium text-bronze">
              Select Premium Membership
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TIERS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 0.08} className="h-full">
                <div
                  data-testid={`tier-card-${t.name.toLowerCase()}`}
                  className={`h-full rounded-2xl bg-cream p-9 text-center flex flex-col transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(140,90,60,0.14)] ${
                    t.highlight
                      ? "border-2 border-gold shadow-[0_20px_45px_rgba(197,160,89,0.22)]"
                      : "border border-bronze/10"
                  }`}
                >
                  {t.highlight && (
                    <span className="mx-auto -mt-1 mb-2 inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-4 py-1.5 text-[0.6rem] uppercase tracking-[0.25em] text-bronze">
                      <Sparkles size={11} /> Most Loved
                    </span>
                  )}
                  <h3 className="font-serif text-xl uppercase tracking-[0.22em] text-ink">{t.name}</h3>
                  <p className="mt-6 font-serif text-4xl sm:text-5xl text-ink">
                    {t.price}
                    <span className="ml-2 align-middle text-xs uppercase tracking-[0.18em] text-stone2 font-sans">
                      / {t.months}
                    </span>
                  </p>
                  <p className="mt-7 text-sm text-stone2">
                    Avail <span className="font-semibold text-ink">{t.therapies}</span> Therapies
                  </p>
                  <a
                    href={purchaseLink(t)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`tier-purchase-${t.name.toLowerCase()}`}
                    className="mt-8 inline-flex items-center justify-center rounded-xl bg-ink px-6 py-4 text-xs uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:bg-bronze"
                  >
                    Purchase
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 text-center text-sm text-stone2">
              Purchase opens a WhatsApp chat with the plan pre-filled — activation is completed at
              the spa.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow className="text-center">Member Privileges</Eyebrow>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-bronze/10 bg-cream p-7 text-center transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-bronze/30">
                  <span className="inline-flex rounded-full bg-bronze/10 p-3.5 text-bronze">
                    <p.icon size={19} />
                  </span>
                  <h3 className="mt-4 font-serif text-xl text-ink">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-stone2">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow className="text-center">How It Works</Eyebrow>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-bronze/10 bg-cream p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-bronze/30">
                  <span className="flex items-center justify-center w-11 h-11 rounded-full border border-gold/60 font-serif text-lg text-bronze">
                    {i + 1}
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-ink">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-stone2">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-3xl bg-ink text-cream p-8 sm:p-12 text-center">
              <h2 className="font-serif font-light text-3xl sm:text-4xl">
                Your calm, <span className="italic text-gold">every single month.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-cream/70 max-w-lg mx-auto">
                Not sure which plan fits? Message us — we will recommend one honestly.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <a
                  href="https://wa.me/919014729756?text=Hi%20Ody%20Luxurious%20Spa%2C%20I%20would%20like%20to%20know%20more%20about%20memberships"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="membership-cta-whatsapp"
                  className="inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-wa-dark"
                >
                  <MessageCircle size={15} /> Ask on WhatsApp
                </a>
                <a
                  href={BRAND.tel}
                  data-testid="membership-cta-call"
                  className="inline-flex items-center gap-3 rounded-full border border-cream/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-cream hover:text-ink"
                >
                  <Phone size={15} /> {BRAND.phoneDisplay}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
